import React from 'react';
import { Autocomplete, TextField, Box, Typography, alpha } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Stable references: Popper rebuilds its popper.js instance when `modifiers`
// changes identity. flip is on by default; preventOverflow also checks the
// cross axis so a tall list stays on screen.
const POPPER_MODIFIERS = [{ name: 'preventOverflow', options: { altAxis: true, padding: 8 } }];
const LISTBOX_PROPS = { style: { maxHeight: 240 } };

interface DosenAutocompleteProps {
  label?: string;
  sx?: any;
  options: any[];
  [key: string]: any; // todas as outras props do MUI
}

export default function DosenAutocomplete({
  label = '',
  sx = {},
  options = [],
  ...props
}: DosenAutocompleteProps) {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        ...sx
      }}
    >
      {/* LABEL */}
      {label && (
        <Typography
          sx={{
            color: 'var(--Text-text-primary, #FDFDFD)',
            fontSize: 14,
            fontFamily: 'Inter',
            fontWeight: 500,
            lineHeight: '20px'
          }}
        >
          {label}
        </Typography>
      )}

      <Autocomplete
        options={options}
        popupIcon={
          <KeyboardArrowDownIcon
            sx={{
              color: 'var(--Stroke-icon-dim, #868686)',
              fontSize: 18
            }}
          />
        }
        // A solid panel (nothing behind bleeds through), capped with its own scroll.
        ListboxProps={LISTBOX_PROPS}
        componentsProps={{
          popper: { modifiers: POPPER_MODIFIERS },
          paper: {
            sx: {
              background: '#1C1C1C !important',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
              borderRadius: '6px',
              border: '1px solid var(--Stroke-Form-Input-border-light, #868686)',
              color: 'var(--Text-text-primary, #FDFDFD)',
              mt: 1,
              '> ul': {
                padding: 0
              },
              '> ul > li': {
                fontFamily: 'Inter',
                fontSize: 14,
                padding: '10px 12px',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                '&:hover': {
                  background: alpha('#F79420', 0.15)
                }
              }
            }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Please Select"
            variant="outlined"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'var(--Fill-Transparent-trans-normal-7, rgba(0, 0, 0, 0.70))',
                borderRadius: '6px',
                outline: '1px solid var(--Stroke-Form-Input-border-light, #868686)',
                outlineOffset: '-1px',
                // minHeight (not height): multi-select chips wrap onto new rows and the
                // field must grow to contain them; right padding clears the popup/clear icons.
                minHeight: '40px',
                padding: '4px 56px 4px 12px',
                color: 'var(--Text-text-primary, #FDFDFD)',
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: 14,
                '& .MuiAutocomplete-input': {
                  color: 'var(--Text-text-primary, #FDFDFD)'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover': {
                  outline: '1px solid #FDFDFD'
                },
                '&.Mui-focused': {
                  outline: '1px solid #F79420'
                }
              },
              '& .MuiInputBase-input': {
                background: 'transparent'
              }
            }}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              sx: {
                gap: '4px',
                '& .MuiAutocomplete-input': {
                  color: 'inherit'
                }
              }
            }}
          />
        )}
        {...props}
      />
    </Box>
  );
}
